// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web) — accès aux API du socle et de la gouvernance.
// Contrat : backend/src/dispositifs/socle/README.md § 3 ; réponses { success, data } ; listes paginées
// ?page=&taille= → { elements, page, taille, total, suivant }. Le serveur est seul juge des droits : une
// erreur (403 compris) est rendue telle qu'il l'envoie (code, message, erreurs).
// ═══════════════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const SOCLE = '/api/dispositifs/socle';
const GOUV = '/api/dispositifs/gouvernance';

/** Tableau défensif (react-component-rules, règle 2). */
export const toArray = (d) => (Array.isArray(d) ? d : (d?.elements || d?.rows || d?.lignes || d?.data || []));

/** Erreur normalisée : { statut, code, message, erreurs }. Message du serveur repris tel quel. */
export function erreurDe(e, defaut = 'Opération impossible. Réessayez.') {
  const r = e?.response;
  if (!r) return { statut: 0, code: 'RESEAU', message: 'Le serveur ne répond pas. Vérifiez la connexion puis réessayez.', erreurs: [] };
  const d = r.data && typeof r.data === 'object' && !(typeof Blob !== 'undefined' && r.data instanceof Blob) ? r.data : {};
  return { statut: r.status, code: d.code || null, message: d.message || defaut, erreurs: Array.isArray(d.erreurs) ? d.erreurs : [] };
}

/** Erreur d'un téléchargement (corps reçu en Blob) : relit le JSON du serveur. */
async function erreurBlob(e) {
  const r = e?.response;
  if (r && typeof Blob !== 'undefined' && r.data instanceof Blob) {
    try { const d = JSON.parse(await r.data.text()); return { statut: r.status, code: d.code || null, message: d.message || 'Téléchargement impossible.', erreurs: d.erreurs || [] }; } catch { /* corps non JSON */ }
  }
  return erreurDe(e, 'Téléchargement impossible.');
}

/** Client lié au jeton de la session (const { token } = useAuth()). */
function client(token) {
  const entetes = token ? { Authorization: `Bearer ${token}` } : {};
  const get = async (url, params) => (await axios.get(url, { params, headers: entetes })).data.data;
  const post = async (url, corps) => (await axios.post(url, corps || {}, { headers: entetes })).data.data;
  return {
    // Socle
    espaces: () => get(`${SOCLE}/moi/espaces`),
    aTraiter: () => get(`${SOCLE}/a-traiter`),
    organisations: (page = 1, taille = 25) => get(`${SOCLE}/organisations`, { page, taille }),
    organisation: (id) => get(`${SOCLE}/organisations/${id}`),
    creerOrganisation: (corps) => post(`${SOCLE}/organisations`, corps),
    validerOrganisation: (id) => post(`${SOCLE}/organisations/${id}/valider`),
    changerEtatOrganisation: (id, vers, motif) => post(`${SOCLE}/organisations/${id}/etat`, { vers, motif }),
    evenementsOrganisation: (id) => get(`${SOCLE}/organisations/${id}/evenements`),
    dispositifs: (orgId, { page = 1, taille = 25, etat } = {}) => get(`${SOCLE}/organisations/${orgId}/dispositifs`, { page, taille, etat: etat || undefined }),
    creerDispositif: (orgId, corps) => post(`${SOCLE}/organisations/${orgId}/dispositifs`, corps),
    dispositif: (id) => get(`${SOCLE}/dispositifs/${id}`),
    transition: (id, vers, motif) => post(`${SOCLE}/dispositifs/${id}/transitions`, motif ? { vers, motif } : { vers }),
    ajouterCapacite: (id, capacite) => post(`${SOCLE}/dispositifs/${id}/capacites`, { capacite }),
    retirerCapacite: (id, capacite, motif) => post(`${SOCLE}/dispositifs/${id}/capacites/${capacite}/desactiver`, { motif }),
    piece: (id) => get(`${SOCLE}/pieces/${id}`),
    deposerPiece: async ({ fichier, organisationId, sujetType, sujetId, typePiece, libelle }) => {
      const f = new FormData();
      f.append('organisation_id', organisationId);
      f.append('sujet_type', sujetType);
      if (sujetId) f.append('sujet_id', sujetId);
      f.append('type_piece', typePiece);
      f.append('libelle', libelle);
      f.append('fichier', fichier);
      return (await axios.post(`${SOCLE}/pieces`, f, { headers: entetes })).data.data;
    },
    /** Contenu d'une pièce (empreinte vérifiée par le serveur) : { blob, type }. */
    fichierPiece: async (id) => {
      try {
        const r = await axios.get(`${SOCLE}/pieces/${id}/fichier`, { headers: entetes, responseType: 'blob' });
        return { blob: r.data, type: r.headers['content-type'] || r.data.type };
      } catch (e) { throw Object.assign(new Error('telechargement'), { normalisee: await erreurBlob(e) }); }
    },
    // Gouvernance
    catalogueFonctions: (type = 'OEUVRES_SOCIALES') => get(`${GOUV}/fonctions/catalogue`, { type }),
    organes: (orgId) => get(`${GOUV}/organisations/${orgId}/organes`),
    creerOrgane: (orgId, corps) => post(`${GOUV}/organisations/${orgId}/organes`, corps),
    validerOrgane: (id) => post(`${GOUV}/organes/${id}/valider`),
    creerFonction: (organeId, corps) => post(`${GOUV}/organes/${organeId}/fonctions`, corps),
    composition: (orgId, date) => get(`${GOUV}/organisations/${orgId}/composition`, { date: date || undefined }),
    titulaireA: (fonctionId, date) => get(`${GOUV}/fonctions/${fonctionId}/titulaire`, { date }),
    mandats: (orgId, { page = 1, taille = 25, statut } = {}) => get(`${GOUV}/organisations/${orgId}/mandats`, { page, taille, statut: statut || undefined }),
    mandat: (id) => get(`${GOUV}/mandats/${id}`),
    enregistrerMandat: (corps) => post(`${GOUV}/mandats`, corps),
    confirmerMandat: (id) => post(`${GOUV}/mandats/${id}/confirmer`),
    annulerMandat: (id, motif) => post(`${GOUV}/mandats/${id}/annuler`, { motif }),
    suspendreMandat: (id, motif) => post(`${GOUV}/mandats/${id}/suspendre`, { motif }),
    leverMandat: (id) => post(`${GOUV}/mandats/${id}/lever`),
    terminerMandat: (id, corps) => post(`${GOUV}/mandats/${id}/terminer`, corps),
    revoquerMandat: (id, corps) => post(`${GOUV}/mandats/${id}/revoquer`, corps),
    // Personnes : liste existante des salariés (population de l'utilisateur, /api/salaries).
    chercherSalaries: async (q) => (await axios.get('/api/salaries', { params: { q, taille: 10, vue: 'actifs' }, headers: entetes })).data,
  };
}

/** Client des dispositifs pour le composant courant. */
export function useDs() {
  const { token } = useAuth();
  return useMemo(() => client(token), [token]);
}

/** Date du jour à Dakar (AAAA-MM-JJ), comme le serveur. */
export function aujourdhuiDakar() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Dakar', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

// ─── Libellés (vocabulaire du métier, jamais de code technique à l'écran) ─────────────────────────
export const TYPES_ORGANISATION = { OEUVRES_SOCIALES: 'Œuvres sociales', COOPERATIVE: 'Coopérative', MUTUELLE: 'Mutuelle', FONDS: 'Fonds' };
export const STATUTS_JURIDIQUES = {
  A_QUALIFIER: 'À qualifier', ASSOCIATION: 'Association', CAISSE_INTERNE: 'Caisse interne', EMANATION_SYNDICALE: 'Émanation syndicale',
  MUTUELLE: 'Mutuelle', COOPERATIVE: 'Coopérative',
};
export const ETATS_ORGANISATION = {
  BROUILLON: ['alerte', 'En préparation'], ACTIVE: ['succes', 'En service'], EN_SOMMEIL: ['neutre', 'En sommeil'], DISSOUTE: ['neutre', 'Dissoute'],
};
export const ETATS_DISPOSITIF = {
  BROUILLON: ['neutre', 'Brouillon'], SIMULE: ['info', 'Simulé'], A_VALIDER: ['alerte', 'À valider'], ACTIF: ['succes', 'Actif'],
  SUSPENDU: ['alerte', 'Suspendu'], ARCHIVE: ['neutre', 'Archivé'],
};
export const CYCLE_DISPOSITIF = ['BROUILLON', 'SIMULE', 'A_VALIDER', 'ACTIF', 'SUSPENDU', 'ARCHIVE'];
export const STATUTS_MANDAT = {
  A_CONFIRMER: ['alerte', 'À confirmer'], EN_COURS: ['succes', 'En cours'], SUSPENDU: ['alerte', 'Suspendu'], TERMINE: ['neutre', 'Terminé'], ANNULE: ['neutre', 'Annulé'],
};
export const NATURES_MANDAT = { TITULAIRE: 'Titulaire', INTERIM: 'Intérim', DELEGATION: 'Délégation' };
export const ORIGINES_MANDAT = {
  ELECTION: 'Élection', NOMINATION: 'Nomination', COOPTATION: 'Cooptation', DESIGNATION_ORGANE: 'Désignation par un organe', DECISION_PRESIDENT: 'Décision du Président',
};
export const MOTIFS_FIN = {
  DEMISSION: 'Démission', FIN_INTERIM: 'Fin de l\'intérim', FIN_CONTRAT: 'Fin du contrat de travail', DECES: 'Décès', ECHEANCE: 'Échéance', AUTRE: 'Autre motif', REVOCATION: 'Révocation',
};
export const TYPES_ORGANES = {
  BUREAU: 'Bureau', COMMISSION_SOCIALE: 'Commission sociale', COMMISSION_CONTROLE: 'Commission de contrôle', ASSEMBLEE_GENERALE: 'Assemblée générale',
  CONSEIL_ADMINISTRATION: 'Conseil d\'administration', COMMISSION_SURVEILLANCE: 'Commission de surveillance', COMMISSION_ACHATS: 'Commission des achats', SECRETARIAT: 'Secrétariat',
};
export const TYPES_DISPOSITIF = {
  PRET: 'Prêt social', AIDE: 'Aide', CAMPAGNE: 'Campagne de vente subventionnée', COTISATION: 'Cotisation périodique', PARTS: 'Parts sociales',
};
export const CAPACITES = {
  PRET_REMBOURSABLE: 'Prêt remboursable', AIDE: 'Aide', CAMPAGNE_ACHAT: 'Campagne d\'achat', COTISATION: 'Cotisation', PARTS: 'Parts sociales',
  RETENUE_PAIE: 'Retenue sur salaire', INSCRIPTION_MOBILE: 'Inscription depuis l\'application', REMISE_QR: 'Remise par carte QR',
};
export const TYPES_PIECE = {
  PV: 'Procès-verbal', STATUTS: 'Statuts', RECEPISSE: 'Récépissé', DECISION: 'Décision', CONTRAT: 'Contrat', DEVIS: 'Devis', BON_COMMANDE: 'Bon de commande',
  FACTURE: 'Facture', AVOIR: 'Avoir', PV_RECEPTION: 'PV de réception', BON_REMISE: 'Bon de remise', RECU: 'Reçu', AVIS_VIREMENT: 'Avis de virement',
  COPIE_CHEQUE: 'Copie de chèque', RELEVE: 'Relevé', JUSTIFICATIF: 'Justificatif', CESSION_GREFFE: 'Cession au greffe', CONVENTION: 'Convention', AUTRE: 'Autre',
};
export const PIECES_MANDAT = ['PV', 'DECISION', 'STATUTS'];
export const ACTIONS_EVENEMENT = {
  CREER: 'Création', VALIDER: 'Validation', CHANGER_ETAT: 'Changement d\'état', SIMULER: 'Simulation', REPRENDRE_BROUILLON: 'Reprise du brouillon',
  SOUMETTRE: 'Soumission à validation', REFUSER: 'Refus de validation', SUSPENDRE: 'Suspension', REPRENDRE: 'Reprise', ARCHIVER: 'Archivage',
  ACTIVER_CAPACITE: 'Capacité ajoutée', DESACTIVER_CAPACITE: 'Capacité retirée', DESACTIVER: 'Désactivation', ENREGISTRER: 'Enregistrement',
  CONFIRMER: 'Confirmation', ANNULER: 'Annulation', LEVER_SUSPENSION: 'Levée de suspension', TERMINER: 'Fin', DECLARER: 'Déclaration',
};
export const OBJETS_EVENEMENT = { ORGANISATION: 'Organisation', ORGANE: 'Organe', FONCTION: 'Fonction', MANDAT: 'Mandat', DISPOSITIF: 'Dispositif' };
export const JOURNAL_MANDAT = {
  ENREGISTREMENT: 'Enregistré', CONFIRMATION: 'Confirmé', SUSPENSION: 'Suspendu', LEVEE_SUSPENSION: 'Suspension levée', FIN: 'Terminé',
  ECHEANCE: 'Terminé à l\'échéance', ANNULATION: 'Annulé', ALERTE_J30: 'Alerte : fin dans 30 jours', ALERTE_J7: 'Alerte : fin dans 7 jours',
};
/** Pouvoirs donnés par une fonction (clés du catalogue IAM), en langage courant ; périmètre d'une délégation. */
export const POUVOIRS = {
  ds_gouvernance_lire: 'Consulter les instances et les mandats', ds_dispositif_lire: 'Consulter les dispositifs',
  ds_dispositif_preparer: 'Préparer un dispositif', ds_dispositif_valider: 'Valider un dispositif', ds_dispositif_suspendre: 'Suspendre ou archiver un dispositif',
  ds_pret_lire: 'Consulter les prêts', ds_pret_saisir_guichet: 'Saisir une demande de prêt au guichet', ds_pret_instruire: 'Instruire un prêt',
  ds_pret_decider: 'Décider d\'un prêt', ds_pret_formalites: 'Formalités d\'un prêt', ds_aide_lire: 'Consulter les aides', ds_aide_motif_lire: 'Lire le motif d\'une aide',
  ds_aide_saisir_guichet: 'Saisir une demande d\'aide au guichet', ds_aide_verifier_pieces: 'Vérifier les pièces d\'une aide', ds_aide_decider: 'Décider d\'une aide',
  ds_campagne_lire: 'Consulter les campagnes', ds_campagne_preparer: 'Préparer une campagne', ds_campagne_valider: 'Valider une campagne',
  ds_inscription_saisir_guichet: 'Inscrire un salarié au guichet', ds_inscription_gerer: 'Gérer les inscriptions', ds_attribution_calculer: 'Calculer l\'attribution',
  ds_attribution_arreter: 'Arrêter l\'attribution', ds_achat_preparer: 'Préparer un achat', ds_achat_valider: 'Valider un achat', ds_reception_signer: 'Signer une réception',
  ds_facture_enregistrer: 'Enregistrer une facture', ds_remise_effectuer: 'Remettre une unité', ds_ecart_declarer: 'Déclarer un écart', ds_ecart_decider: 'Décider d\'un écart',
  ds_finance_lire: 'Consulter la trésorerie et les créances', ds_paiement_preparer: 'Préparer un paiement', ds_paiement_signer: 'Signer un paiement', ds_paiement_executer: 'Exécuter un paiement', ds_campagne_passer_reel: 'Passer une campagne au réel',
  ds_paiement_second_signer: 'Contresigner un paiement', ds_cheque_gerer: 'Gérer les chèques', ds_ecriture_saisir: 'Saisir une écriture', ds_ecriture_valider: 'Valider une écriture',
  ds_contrepassation_proposer: 'Proposer une contrepassation', ds_contrepassation_valider: 'Valider une contrepassation',
  ds_creance_mouvement_proposer: 'Proposer un mouvement de créance', ds_creance_mouvement_valider: 'Valider un mouvement de créance', ds_caisse_arreter: 'Arrêter la caisse',
  ds_rapprochement_etablir: 'Établir un rapprochement bancaire', ds_rapprochement_controler: 'Contrôler un rapprochement', ds_periode_cloturer_confirmer: 'Confirmer une clôture de période',
  ds_export_comptable: 'Produire l\'export comptable', ds_fondement_enregistrer: 'Enregistrer un fondement de retenue', ds_fondement_confirmer: 'Confirmer un fondement de retenue',
  ds_retenues_lire: 'Consulter les retenues', ds_retenues_arreter_demander: 'Demander l\'arrêt des retenues', ds_retenues_arreter_confirmer: 'Confirmer l\'arrêt des retenues',
  ds_rapport_lire: 'Consulter les rapports', ds_rapport_arreter: 'Arrêter un rapport', ds_export_nominatif: 'Consulter le détail nominatif des états',
};
export const libelle = (table, code) => {
  const v = table[code];
  if (Array.isArray(v)) return v[1];
  return v || code || '';
};

/** Taille lisible d'un fichier. */
export const taillePiece = (o) => (o == null ? '' : o < 1024 ? `${o} o` : o < 1048576 ? `${Math.round(o / 1024)} Ko` : `${(o / 1048576).toFixed(1).replace('.', ',')} Mo`);
/** Date et heure lisibles (Dakar). */
export const dateHeure = (d) => (d ? new Intl.DateTimeFormat('fr-FR', { timeZone: 'Africa/Dakar', dateStyle: 'short', timeStyle: 'short' }).format(new Date(d)) : '');
